import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { invalidatePatientData } from "@/portals/patient/api/patientQueries";
import { useFamily } from "@/portals/patient/context/FamilyContext";
import { useUploadHistory } from "@/portals/patient/context/UploadHistoryContext";

// ParsedRecordsContext fetches once, when its provider first mounts (login
// time) — it has no way of knowing a new upload has since finished
// processing. UploadHistoryContext is the one place that *does* know (it
// polls upload status), so this hook watches it for a processing -> complete
// transition and, when one happens, tells ParsedRecordsContext to refetch.
// Medications/DiagnosticReports/Visits are pure derived selectors over
// ParsedRecordsContext's data (see those files), so refreshing it alone is
// enough — they recompute automatically via useMemo once `records` updates.
// Without this, the only way to see new data is a full page reload, which
// remounts every provider from scratch. Every new fetch-once context added to
// this app should be wired in here the same way — see the app-wide
// instant-reactivity preference this hook exists to satisfy.
//
// It invalidates without subscribing (2026-10-23, connection plan step 4):
// it used to read the records query itself, and since it runs in the patient
// layout that kept the every-type records request live on EVERY page, so each
// invalidation refetched the app's heaviest request even where nothing showed
// records. Now a page that shows records refetches at once; any other page
// just has them marked stale, fetched fresh the moment such a page opens.
export function useAutoRefreshClinicalData() {
  const { zipUploads } = useUploadHistory();
  const { activeDependent } = useFamily();
  const client = useQueryClient();
  const profileId = activeDependent?.id;
  const prevStatuses = useRef<Record<string, string>>({});

  useEffect(() => {
    let justCompleted = false;
    for (const entry of zipUploads) {
      const prevStatus = prevStatuses.current[entry.id];
      prevStatuses.current[entry.id] = entry.status;
      if (prevStatus && prevStatus !== "complete" && entry.status === "complete") {
        justCompleted = true;
      }
    }
    if (justCompleted) {
      void invalidatePatientData(client, profileId);
    }
  }, [zipUploads, client, profileId]);
}
